import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col } from 'react-bootstrap';
import { Empty, Spin, Button, Tooltip, message, Select, DatePicker } from 'antd';
import dayjs from 'dayjs';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

import Card from '../../../components/bootstrap/card';
import { getChartsService, getGraphViewDataService } from '../../../services/GraphService';
import GraphView from './GraphView';
import Loader from '../../../components/Loader';
import './graphs.css';

import {
    BarChartOutlined,
    LineChartOutlined,
    AreaChartOutlined,
    PieChartOutlined,
    RadarChartOutlined,
    DotChartOutlined,
    AppstoreOutlined,
    RadiusSettingOutlined,
    DatabaseOutlined,
    CheckCircleOutlined,
    DownloadOutlined
} from '@ant-design/icons';

const CHART_TYPES = [
    { value: 'bar', label: 'Bar Chart', icon: <BarChartOutlined /> },
    { value: 'line', label: 'Line Chart', icon: <LineChartOutlined /> },
    { value: 'area', label: 'Area Chart', icon: <AreaChartOutlined /> },
    { value: 'pie', label: 'Pie Chart', icon: <PieChartOutlined /> },
    { value: 'radar', label: 'Radar Chart', icon: <RadarChartOutlined /> },
    { value: 'doughnut', label: 'Doughnut Chart', icon: <DotChartOutlined /> },
    { value: 'composed', label: 'Composed Chart', icon: <AppstoreOutlined /> },
    { value: 'radial', label: 'Radial Chart', icon: <RadiusSettingOutlined /> },
];

/* ── format date string nicely: "2024-01-01" → "01 Jan 2024" ── */
const fmtDate = (d) => {
    if (!d) return null;
    const parsed = new Date(d);
    if (isNaN(parsed)) return d; // fallback
    return parsed.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/**
 * KPI Stat Card Component
 */
const StatCard = ({ title, value, icon, color, subtext }) => (
    <div className="stat-card">
        <div className="stat-card-header">
            <div>
                <div className="stat-card-title">{title}</div>
                <div className="stat-card-value">{value}</div>
            </div>
            <div
                className="stat-card-icon"
                style={{
                    background: color + '20',
                    color: color
                }}
            >
                {icon}
            </div>
        </div>
        {subtext && (
            <div className="stat-card-subtext">
                {subtext}
            </div>
        )}
    </div>
);

/**
 * GraphsDashboard - Displays active charts on the dashboard
 */
const GraphsDashboard = () => {
    const [charts, setCharts] = useState([]);
    const [chartsData, setChartsData] = useState({});
    const [loading, setLoading] = useState(true);
    const token = localStorage.getItem('token');
    const adminName = localStorage.getItem('name') || 'Admin';

    // ── Duration filter (same behaviour as the main dashboard) ──────────────────
    const currentYear = dayjs().year();
    const [dateMode, setDateMode] = useState('till_date');
    const [selectedFYYear, setSelectedFYYear] = useState(dayjs());
    const [selectedCYYear, setSelectedCYYear] = useState(dayjs());
    const [dates, setDates] = useState(null);

    const handleDateModeChange = (modeVal) => {
        setDateMode(modeVal);
        if (modeVal === 'current_year') {
            setDates([dayjs(`${currentYear}-01-01`), dayjs(`${currentYear}-12-31`)]);
        } else if (modeVal === 'last_year') {
            const ly = currentYear - 1;
            setDates([dayjs(`${ly}-01-01`), dayjs(`${ly}-12-31`)]);
        } else if (modeVal === 'till_date') {
            setDates(null);
        } else if (modeVal === 'fy') {
            const yr = selectedFYYear ? selectedFYYear.year() : currentYear;
            setDates([dayjs(`${yr}-04-01`), dayjs(`${yr + 1}-03-31`)]);
        } else if (modeVal === 'cy') {
            const yr = selectedCYYear ? selectedCYYear.year() : currentYear;
            setDates([dayjs(`${yr}-01-01`), dayjs(`${yr}-12-31`)]);
        } else if (modeVal === 'custom') {
            if (!dates) setDates([dayjs(`${currentYear}-01-01`), dayjs(`${currentYear}-12-31`)]);
        }
    };

    const handleFYYearChange = (val) => {
        setSelectedFYYear(val);
        if (val) {
            const yr = val.year();
            setDates([dayjs(`${yr}-04-01`), dayjs(`${yr + 1}-03-31`)]);
        }
    };

    const handleCYYearChange = (val) => {
        setSelectedCYYear(val);
        if (val) {
            const yr = val.year();
            setDates([dayjs(`${yr}-01-01`), dayjs(`${yr}-12-31`)]);
        } else {
            setDates(null);
        }
    };

    // Fetch the active chart list (metadata only)
    const fetchCharts = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getChartsService(token);
            if (response.status === 'success') {
                const activeCharts = (response.data || []).filter(c => c.is_active);
                setCharts(activeCharts);
            }
        } catch (error) {
            console.error('Failed to load charts:', error);
        } finally {
            setLoading(false);
        }
    }, [token]);

    // Fetch data for a single chart using the page's selected duration (not the saved range).
    const fetchChartData = useCallback(async (chart) => {
        try {
            const from = dates && dates[0] ? dayjs(dates[0]).format('YYYY-MM-DD') : null;
            const to = dates && dates[1] ? dayjs(dates[1]).format('YYYY-MM-DD') : null;
            const config = {
                report_type: chart.report_type,
                x_axis_column: chart.x_axis_column,
                y_axis_columns: chart.y_axis_columns,
                date_column: chart.date_column,
                date_aggregation: chart.date_aggregation,
                date_from: from,
                date_to: to,
            };
            const response = await getGraphViewDataService(token, config);
            if (response.status === 'success') {
                setChartsData(prev => ({
                    ...prev,
                    [chart.chart_id]: {
                        data: response.data || [],
                        columnTitles: response.column_titles || {}
                    }
                }));
            }
        } catch (error) {
            console.error(`Error fetching data for chart ${chart.chart_id}:`, error);
        }
    }, [token, dates]);

    useEffect(() => {
        fetchCharts();
    }, [fetchCharts]);

    // Re-fetch every chart's data when the list loads or the selected duration changes.
    useEffect(() => {
        charts.forEach(chart => fetchChartData(chart));
    }, [charts, fetchChartData]);

    const getChartIcon = (type) => {
        const chart = CHART_TYPES.find(c => c.value === type);
        return chart?.icon || '📊';
    };

    /* ── PDF helpers ── */

    /** Derive Y-axis column keys from data rows (mirrors GraphView yColumns logic) */
    const getYColumns = (data) => {
        if (!data || data.length === 0) return [];
        return Object.keys(data[0]).filter(k => k !== 'name' && k !== '_total');
    };

    /** Format a column key to a readable label (mirrors GraphView formatColumnLabel) */
    const resolveColumnLabel = (key, columnTitles = {}) => {
        if (!key) return '';
        const parts = key.split('_');
        if (parts.length >= 2) {
            let agg = parts[parts.length - 1];
            let dataIndexParts = parts.slice(0, -1);
            if (parts.length >= 3 && parts[parts.length - 2] === 'percentage') {
                agg = 'percentage_' + agg;
                dataIndexParts = parts.slice(0, -2);
            }
            const knownAggs = ['sum', 'avg', 'count', 'min', 'max', 'distinct', 'percentage_count', 'percentage_sum'];
            if (knownAggs.includes(agg)) {
                const dataIndex = dataIndexParts.join('_');
                const aggLabel = {
                    sum: 'Sum', avg: 'Avg', count: 'Count', min: 'Min', max: 'Max',
                    distinct: 'Distinct', percentage_count: '% Count', percentage_sum: '% Sum'
                }[agg] || agg;
                const colTitle = columnTitles[dataIndex] ||
                    dataIndex.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                return `${colTitle} (${aggLabel})`;
            }
        }
        return columnTitles[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    /** Format large numbers the same way the chart tooltip does */
    const formatNum = (value) => {
        if (typeof value !== 'number') return String(value ?? '');
        if (value >= 10000000) return `${(value / 10000000).toFixed(2)}Cr`;
        if (value >= 100000) return `${(value / 100000).toFixed(2)}L`;
        if (value >= 1000) return `${(value / 1000).toFixed(2)}K`;
        return value.toLocaleString('en-IN', { maximumFractionDigits: 2 });
    };

    /**
     * Draw a styled data table into an existing jsPDF document.
     * Returns the Y coordinate after the table.
     */
    // rowH and headerH are passed in from handleDownload (computed to fit single page)
    const drawDataTable = (pdf, data, columnTitles, startY, margin, pageWidth, rowH, headerH) => {
        if (!data || data.length === 0) return startY;

        const yColumns = getYColumns(data);
        const usableW  = pageWidth - margin * 2;
        const serialW  = 10;
        const nameW    = Math.min(62, usableW * 0.28);
        const yColW    = (usableW - serialW - nameW) / Math.max(yColumns.length, 1);

        // Section heading
        pdf.setFontSize(9.5);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(60, 52, 137);
        pdf.text('DATA TABLE', margin, startY + 5.5);
        pdf.setDrawColor(60, 52, 137);
        pdf.setLineWidth(0.5);
        pdf.line(margin, startY + 7.5, margin + 30, startY + 7.5);

        let curY = startY + 11;

        const drawRow = (cells, isHeader, isTotal, rowIndex) => {
            const h  = isHeader ? headerH : rowH;
            const x0 = margin;

            // Background
            if (isHeader) {
                pdf.setFillColor(60, 52, 137);
            } else if (isTotal) {
                pdf.setFillColor(232, 230, 255);
            } else {
                const even = rowIndex % 2 === 0;
                pdf.setFillColor(even ? 255 : 246, even ? 255 : 247, even ? 255 : 254);
            }
            pdf.rect(x0, curY, usableW, h, 'F');

            // Bottom border
            pdf.setDrawColor(210, 208, 228);
            pdf.setLineWidth(0.2);
            pdf.line(x0, curY + h, x0 + usableW, curY + h);

            // Vertical column separators
            pdf.setDrawColor(210, 208, 228);
            pdf.setLineWidth(0.15);
            let sepX = x0 + serialW;
            pdf.line(sepX, curY, sepX, curY + h);
            sepX += nameW;
            pdf.line(sepX, curY, sepX, curY + h);
            yColumns.forEach((_, ci) => {
                if (ci < yColumns.length - 1) {
                    sepX += yColW;
                    pdf.line(sepX, curY, sepX, curY + h);
                }
            });

            // Font size scales with row height — bigger rows = bigger text
            const fontSize = isHeader
                ? Math.max(7, Math.min(9, h * 0.78))
                : (isTotal
                    ? Math.max(6.5, Math.min(8.5, h * 0.75))
                    : Math.max(6, Math.min(8, h * 0.72)));

            pdf.setFontSize(fontSize);
            pdf.setFont(undefined, isHeader || isTotal ? 'bold' : 'normal');

            if (isHeader)       pdf.setTextColor(255, 255, 255);
            else if (isTotal)   pdf.setTextColor(60, 52, 137);
            else                pdf.setTextColor(40, 40, 55);

            const colWidths = [serialW, nameW, ...yColumns.map(() => yColW)];
            let cx = x0;
            cells.forEach((cell, i) => {
                const cw       = colWidths[i] || yColW;
                const textY    = curY + h * 0.63;
                const maxChars = Math.floor(cw / (fontSize / 5.8));
                const cellText = String(cell ?? '').slice(0, maxChars);
                if (i >= 2) {
                    pdf.text(cellText, cx + cw - 2.5, textY, { align: 'right' });
                } else {
                    pdf.text(cellText, cx + 2.5, textY);
                }
                cx += cw;
            });

            curY += h;
        };

        const headers = ['#', 'Name', ...yColumns.map(k => resolveColumnLabel(k, columnTitles))];
        drawRow(headers, true, false, -1);

        data.forEach((item, rowIndex) => {
            const cells = [
                String(rowIndex + 1),
                String(item.name ?? ''),
                ...yColumns.map(k => formatNum(item[k]))
            ];
            drawRow(cells, false, false, rowIndex);
        });

        const totals = [
            '', 'TOTAL',
            ...yColumns.map(k => {
                const sum = data.reduce((acc, item) => acc + (Number(item[k]) || 0), 0);
                return formatNum(sum);
            })
        ];
        drawRow(totals, false, true, -1);

        return curY + 4;
    };

    const handleDownload = async (chart) => {
        const container = document.getElementById(`chart-container-${chart.chart_id}`);
        if (!container) return;

        try {
            message.loading({ content: 'Generating PDF...', key: 'download' });

            const canvas = await html2canvas(container, {
                backgroundColor: '#ffffff',
                scale: 2,
                logging: false,
                useCORS: true
            });

            const imgData = canvas.toDataURL('image/png');

            // A4 Landscape: 297 × 210 mm
            const pdf        = new jsPDF('l', 'mm', 'a4');
            const pageWidth  = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin       = 12;
            const headerHeight = 26;
            const gapBelowHeader = 5;

            /* ── Branded header ── */
            pdf.setFillColor(60, 52, 137);
            pdf.rect(0, 0, pageWidth, headerHeight, 'F');

            // Top accent stripe
            pdf.setFillColor(99, 94, 186);
            pdf.rect(0, 0, pageWidth, 2.5, 'F');

            // Admin / company name
            pdf.setFontSize(14);
            pdf.setFont(undefined, 'bold');
            pdf.setTextColor(255, 255, 255);
            pdf.text(adminName, margin + 3, 12);

            // Chart name
            pdf.setFontSize(9.5);
            pdf.setFont(undefined, 'normal');
            pdf.setTextColor(200, 200, 240);
            pdf.text(chart.chart_name || 'Chart', margin + 3, 21);

            // Report meta — right side
            pdf.setFontSize(8);
            pdf.setTextColor(200, 200, 240);
            pdf.text(
                `Report: ${chart.report_type || 'N/A'}${chart.creator_name ? `  |  Created by: ${chart.creator_name}` : ''}`,
                pageWidth - margin, 11, { align: 'right' }
            );

            // Reflect the page's active duration in the export (not the chart's saved range).
            const fromStr = dates && dates[0] ? fmtDate(dayjs(dates[0]).format('YYYY-MM-DD')) : null;
            const toStr   = dates && dates[1] ? fmtDate(dayjs(dates[1]).format('YYYY-MM-DD')) : null;
            const dateStr = fromStr && toStr ? `${fromStr}  –  ${toStr}`
                : fromStr ? `From ${fromStr}`
                    : toStr  ? `Until ${toStr}`
                        : null;

            pdf.setFontSize(8);
            pdf.text(
                dateStr || `Generated: ${new Date().toLocaleString('en-IN')}`,
                pageWidth - margin, 20, { align: 'right' }
            );

            /* ── Layout constants ── */
            const contentY  = headerHeight + gapBelowHeader;  // chart starts here on p1
            const bottomY   = pageHeight - margin;             // bottom of usable area
            const maxW      = pageWidth - margin * 2;

            const chartData    = chartsData[chart.chart_id]?.data || [];
            const columnTitles = chartsData[chart.chart_id]?.columnTitles || {};
            const hasTable     = chartData.length > 0;

            /* ── Page 1: Chart — full height below header ── */
            const chartAreaH = bottomY - contentY;   // entire remaining page height
            const aspect = canvas.width / canvas.height;
            let imgW = maxW;
            let imgH = imgW / aspect;
            if (imgH > chartAreaH) { imgH = chartAreaH; imgW = imgH * aspect; }

            pdf.addImage(
                imgData, 'PNG',
                margin + (maxW - imgW) / 2,
                contentY + (chartAreaH - imgH) / 2,   // vertically centred in available area
                imgW, imgH
            );

            if (!hasTable) {
                pdf.save(`${chart.chart_name || 'chart'}.pdf`);
                message.success({ content: 'Chart PDF downloaded', key: 'download' });
                return;
            }

            /* ── Page 2: Data table — full page ── */
            pdf.addPage();

            const p2HeaderH = 18;

            // Compact branded header
            pdf.setFillColor(60, 52, 137);
            pdf.rect(0, 0, pageWidth, p2HeaderH, 'F');
            pdf.setFillColor(99, 94, 186);
            pdf.rect(0, 0, pageWidth, 2.5, 'F');

            pdf.setFontSize(11);
            pdf.setFont(undefined, 'bold');
            pdf.setTextColor(255, 255, 255);
            pdf.text(`${chart.chart_name || 'Chart'}  —  Data Table`, margin + 3, 12);

            pdf.setFontSize(8.5);
            pdf.setFont(undefined, 'normal');
            pdf.setTextColor(200, 200, 240);
            pdf.text(adminName, pageWidth - margin, 12, { align: 'right' });

            // Dynamic row sizing to fill the full page
            const tableStartY  = p2HeaderH + 4;
            const tableBottomY = pageHeight - margin;
            const tableAreaH   = tableBottomY - tableStartY;

            const headingBlockH = 12;
            const tblHeaderH    = 12;
            const dataAreaH     = tableAreaH - headingBlockH - tblHeaderH;
            const rowCount      = chartData.length + 1;   // data rows + totals row
            // Fill the page — clamp between 6mm and 14mm per row
            const rowH = Math.max(6, Math.min(14, dataAreaH / rowCount));

            drawDataTable(
                pdf, chartData, columnTitles,
                tableStartY, margin, pageWidth,
                rowH, tblHeaderH
            );

            pdf.save(`${chart.chart_name || 'chart'}.pdf`);
            message.success({ content: 'Chart PDF downloaded', key: 'download' });

        } catch (error) {
            console.error('Download failed', error);
            message.error({ content: 'Failed to download chart', key: 'download' });
        }
    };

    if (loading) {
        return <Loader show={loading} />;
    }

    if (charts.length === 0) {
        return (
            <Card>
                <Card.Body>
                    <Empty
                        description="No active charts to display"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                </Card.Body>
            </Card>
        );
    }

    const reportTypesCount = [...new Set(charts.map(c => c.report_type))].length;

    return (
        <div className="dashboard-charts-container">
            {/* Duration filter — all charts below render for this range */}
            <div
                className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4 px-3 py-2"
                style={{
                    backgroundColor: 'var(--cip-surface, #ffffff)',
                    border: '1px solid var(--cip-border, #e2e8f0)',
                    borderRadius: '12px',
                    boxShadow: 'var(--cip-shadow-sm, 0 1px 3px rgba(0,0,0,0.05))'
                }}
            >
                <h4 className="mb-0" style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>
                    Executive Dashboard Visuals
                </h4>
                <div className="d-flex align-items-center gap-2">
                    <span className="text-secondary" style={{ fontSize: '13px', fontWeight: 600 }}>Duration:</span>
                    <Select
                        value={dateMode}
                        onChange={handleDateModeChange}
                        style={{ width: 150 }}
                        size="middle"
                    >
                        <Select.Option value="current_year">Current Year</Select.Option>
                        <Select.Option value="last_year">Last Year</Select.Option>
                        <Select.Option value="fy">Financial Year</Select.Option>
                        <Select.Option value="cy">Calendar Year</Select.Option>
                        <Select.Option value="custom">Custom</Select.Option>
                    </Select>
                    {dateMode === 'fy' && (
                        <DatePicker
                            picker="year"
                            placeholder="Select FY"
                            value={selectedFYYear}
                            onChange={handleFYYearChange}
                            size="middle"
                            style={{ width: '130px' }}
                            format={(val) => (val ? `FY ${val.year()}-${(val.year() + 1).toString().slice(-2)}` : '')}
                        />
                    )}
                    {dateMode === 'cy' && (
                        <DatePicker
                            picker="year"
                            placeholder="Select Year"
                            value={selectedCYYear}
                            onChange={handleCYYearChange}
                            size="middle"
                            style={{ width: '130px' }}
                            format={(val) => (val ? `${val.year()}` : '')}
                        />
                    )}
                    {dateMode === 'custom' && (
                        <DatePicker.RangePicker
                            value={dates}
                            onChange={(val) => setDates(val || [dayjs(`${currentYear}-01-01`), dayjs(`${currentYear}-12-31`)])}
                            style={{ width: '260px' }}
                            size="middle"
                        />
                    )}
                </div>
            </div>

            {/* KPI Section */}
            <Row className="mb-4">
                <Col md={4} className="mb-3 mb-md-0">
                    <StatCard
                        title="Average Data Points"
                        value={(() => {
                            const chartsCount = Object.keys(chartsData).length;
                            const totalPoints = Object.values(chartsData).reduce((acc, curr) => acc + (curr?.data?.length || 0), 0);
                            return chartsCount > 0 ? Math.round(totalPoints / chartsCount).toLocaleString() : '0';
                        })()}
                        icon={<LineChartOutlined />}
                        color="#25dbcd"
                        subtext={<span><CheckCircleOutlined style={{ color: '#10b981' }} /> Across charts</span>}
                    />
                </Col>
                <Col md={4} className="mb-3 mb-md-0">
                    <StatCard
                        title="Data Sources"
                        value={reportTypesCount}
                        icon={<DatabaseOutlined />}
                        color="#ec4899"
                        subtext="Across different reports"
                    />
                </Col>
                <Col md={4}>
                    <StatCard
                        title="Key Metrics"
                        value={[...new Set(charts.flatMap(c => c.y_axis_columns || []))].length}
                        icon={<BarChartOutlined />}
                        color="#f59e0b"
                        subtext="Unique variables tracked"
                    />
                </Col>
            </Row>

            <Row>
                {charts.map((chart, index) => {
                    const isFeatured = index === 0 && charts.length % 2 !== 0;

                    return (
                        <Col key={chart.chart_id} lg={6} className="chart-display-card-col">
                            <Card className="chart-card">
                                <Card.Header className="chart-card-header d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center" style={{ gap: 10, minWidth: 0 }}>
                                        <div style={{
                                            padding: 7,
                                            borderRadius: 8,
                                            background: '#f8fafc',
                                            color: '#64748b',
                                            flexShrink: 0,
                                            display: 'inline-flex'
                                        }}>
                                            {getChartIcon(chart.chart_type)}
                                        </div>
                                        <div style={{ minWidth: 0 }}>
                                            <h5
                                                className="chart-card-title"
                                                style={{ margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                                title={chart.chart_name}
                                            >
                                                {chart.chart_name}
                                            </h5>
                                            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {chart.report_type}
                                            </div>
                                        </div>
                                    </div>
                                    <Tooltip title="Download PDF">
                                        <Button
                                            type="text"
                                            icon={<DownloadOutlined />}
                                            onClick={() => handleDownload(chart)}
                                            size="small"
                                            style={{ color: '#94a3b8', flexShrink: 0 }}
                                        />
                                    </Tooltip>
                                </Card.Header>
                                <Card.Body className="chart-card-body" style={{ minHeight: 300 }}>
                                    {chartsData[chart.chart_id] ? (
                                        <div id={`chart-container-${chart.chart_id}`} style={{ background: '#fff', padding: 8 }}>
                                            <GraphView
                                                chartType={chart.chart_type}
                                                data={chartsData[chart.chart_id]?.data}
                                                columnTitles={chartsData[chart.chart_id]?.columnTitles}
                                                yAxisColumns={chart.y_axis_columns}
                                                height={isFeatured ? 400 : 350}
                                                compact={true}
                                                showMetricOverlay={true}
                                                showDownload={false}
                                            />
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: isFeatured ? 400 : 350 }}>
                                            <Spin tip="Loading data..." />
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    );
                })}
            </Row>
            <Loader show={loading} />
        </div>
    );
};

export default GraphsDashboard;
